import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { OpenSearchFieldMapper } from './opensearch-field-mapper';
import { DateGroupingUtility, type DateGroupingInterval } from './date-grouping';
import type { ChartDataPoint } from 'tio-common/types/chart';

/**
 * ChartDataTransformer - Utility for transforming OpenSearch responses into chart data
 *
 * This utility handles the transformation of OpenSearch aggregation and document data
 * into the standardized ChartDataPoint format used by chart components.
 */
export class ChartDataTransformer {
  /**
   * Transform OpenSearch response using the response builder pattern
   */
  static fromOpenSearchResponse(
    responseBuilder: OpenSearchResponseBuilder,
    fieldName: string,
    options: {
      isDate?: boolean;
      isTimestamp?: boolean;
      dateGrouping?: DateGroupingInterval;
      sumField?: string; // Field to sum instead of counting
      preserveOrder?: boolean; // Preserve bucket order (for range aggregations)
    } = {}
  ): ChartDataPoint[] {
    const {
      isDate = false,
      isTimestamp = false,
      dateGrouping = 'day',
      sumField,
      preserveOrder = false,
    } = options;

    try {
      // Try to get aggregation data first (preferred method)
      const buckets = ChartDataTransformer.getAggregationBuckets(responseBuilder, fieldName);

      if (buckets.length > 0) {
        return ChartDataTransformer.transformAggregationBuckets(buckets, {
          isDate,
          isTimestamp,
          dateGrouping,
          sumField,
          preserveOrder,
        });
      }

      // Fallback to document extraction if no aggregations
      const documents = responseBuilder.getDocuments() as Record<string, unknown>[];
      return ChartDataTransformer.transformDocumentsToChartData(documents, fieldName, {
        isDate,
        isTimestamp,
        dateGrouping,
      });
    } catch (error) {
      console.error('Error transforming OpenSearch response:', error);

      // Final fallback to documents
      const documents = responseBuilder.getDocuments() as Record<string, unknown>[];
      return ChartDataTransformer.transformDocumentsToChartData(documents, fieldName, {
        isDate,
        isTimestamp,
        dateGrouping,
      });
    }
  }

  /**
   * Get aggregation buckets from response builder with flexible key matching
   */
  private static getAggregationBuckets(
    responseBuilder: OpenSearchResponseBuilder,
    fieldName: string
  ): Array<{ key: string | number; count: number; aggregations?: Record<string, unknown> }> {
    // Try different possible aggregation key variations
    const possibleKeys = OpenSearchFieldMapper.getAggregationKeyVariations(fieldName);

    for (const key of possibleKeys) {
      const buckets = responseBuilder.getTermsAggregation(key);
      if (buckets.length > 0) {
        return buckets;
      }
    }

    return [];
  }

  /**
   * Transform aggregation buckets into chart data points
   */
  private static transformAggregationBuckets(
    buckets: Array<{ key: string | number; count: number; aggregations?: Record<string, unknown> }>,
    options: {
      isDate?: boolean;
      isTimestamp?: boolean;
      dateGrouping?: DateGroupingInterval;
      sumField?: string;
      preserveOrder?: boolean;
    }
  ): ChartDataPoint[] {
    const {
      isDate = false,
      isTimestamp = false,
      dateGrouping = 'day',
      sumField,
      preserveOrder = false,
    } = options;

    const chartData = buckets.map((bucket) => {
      let categoryValue = String(bucket.key);

      // Handle date/timestamp formatting
      if (isDate || isTimestamp) {
        categoryValue = DateGroupingUtility.groupTimestampByInterval(
          String(bucket.key),
          dateGrouping
        );
      } else {
        // For non-date fields, format the category name
        categoryValue = ChartDataTransformer.formatCategoryValue(categoryValue);
      }

      // Check if we have a sum aggregation in the bucket
      let value = bucket.count;
      if (sumField && bucket.aggregations) {
        // Look for a sum aggregation with the sumField name
        const sumAggKey = `${sumField}_sum`;
        const sumAgg = bucket.aggregations[sumAggKey] as { value?: number } | undefined;
        if (sumAgg && typeof sumAgg.value === 'number') {
          value = sumAgg.value;
        }
      }

      return {
        category: categoryValue,
        value,
        count: bucket.count,
      };
    });

    // Group by category if we have timestamp fields (to handle multiple timestamps in same interval)
    if (isTimestamp) {
      return ChartDataTransformer.groupDataByCategory(chartData);
    }

    // Preserve original bucket order for range aggregations
    if (preserveOrder) {
      return chartData;
    }

    return ChartDataTransformer.sortChartData(chartData, isDate || isTimestamp);
  }

  /**
   * Transform documents into chart data points by grouping by field value
   */
  static transformDocumentsToChartData(
    documents: Record<string, unknown>[],
    fieldName: string,
    options: {
      isDate?: boolean;
      isTimestamp?: boolean;
      dateGrouping?: DateGroupingInterval;
    } = {}
  ): ChartDataPoint[] {
    const { isDate = false, isTimestamp = false, dateGrouping = 'day' } = options;
    const groupedData = new Map<string, number>();

    documents.forEach((doc: Record<string, unknown>) => {
      let categoryValue: string = String(doc[fieldName] || 'Unknown');

      if (isDate || isTimestamp) {
        categoryValue = DateGroupingUtility.groupTimestampByInterval(categoryValue, dateGrouping);
      } else {
        categoryValue = ChartDataTransformer.formatCategoryValue(categoryValue);
      }

      const currentCount = groupedData.get(categoryValue) || 0;
      groupedData.set(categoryValue, currentCount + 1);
    });

    const chartData = Array.from(groupedData.entries()).map(([category, count]) => ({
      category,
      value: count,
      count,
    }));

    return ChartDataTransformer.sortChartData(chartData, isDate || isTimestamp);
  }

  /**
   * Format category values for better display
   */
  static formatCategoryValue(value: string): string {
    // Handle empty or undefined values
    if (!value || value === 'Unknown' || value.trim() === '') {
      return 'Unknown';
    }

    // Special case: n_a → N/A
    if (value === 'n_a') {
      return 'N/A';
    }

    // Remove common prefixes
    let cleanValue = value.replace('TAS.ApplicationState.', '').replace('PSLF.', '');

    // Detect SCREAMING_SNAKE_CASE (e.g. DEFINE_COURSES_AND_SCHOLARSHIPS)
    // and convert to title case directly without the camelCase splitter
    const isScreamingSnake = /^[A-Z][A-Z0-9_]+$/.test(cleanValue);

    if (isScreamingSnake) {
      return cleanValue
        .split('_')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }

    // For camelCase or mixed case: add spaces before capitals, convert underscores
    cleanValue = cleanValue
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Only split on camelCase boundaries
      .trim();

    // Title case: capitalize first letter of each word
    return cleanValue
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Group chart data by category, summing values
   */
  private static groupDataByCategory(chartData: ChartDataPoint[]): ChartDataPoint[] {
    const groupedData = new Map<string, number>();

    chartData.forEach((item) => {
      const existing = groupedData.get(item.category) || 0;
      groupedData.set(item.category, existing + (item.count ?? 0));
    });

    return Array.from(groupedData.entries()).map(([category, count]) => ({
      category,
      value: count,
      count,
    }));
  }

  /**
   * Sort chart data appropriately based on data type
   */
  private static sortChartData(
    chartData: ChartDataPoint[],
    isDateField: boolean
  ): ChartDataPoint[] {
    if (isDateField) {
      return DateGroupingUtility.sortChartDataByDate(chartData);
    }

    // For non-date fields, sort alphabetically by category
    return chartData.sort((a, b) => a.category.localeCompare(b.category));
  }

  /**
   * Calculate percentages for chart data (useful for pie charts)
   */
  static calculatePercentages(
    chartData: ChartDataPoint[]
  ): Array<ChartDataPoint & { percentage: number }> {
    const total = chartData.reduce((sum, item) => sum + item.value, 0);

    return chartData.map((item) => ({
      ...item,
      percentage: total > 0 ? (item.value / total) * 100 : 0,
    }));
  }

  /**
   * Filter chart data to top N items, grouping the rest as "Other"
   */
  static topN(chartData: ChartDataPoint[], n: number): ChartDataPoint[] {
    if (chartData.length <= n) {
      return chartData;
    }

    // Sort by value descending to get top items
    const sorted = [...chartData].sort((a, b) => b.value - a.value);
    const topItems = sorted.slice(0, n);
    const otherItems = sorted.slice(n);

    // Sum up the "other" items
    const otherTotal = otherItems.reduce((sum, item) => sum + item.value, 0);

    if (otherTotal > 0) {
      topItems.push({
        category: 'Other',
        value: otherTotal,
        count: otherTotal,
      });
    }

    return topItems;
  }

  /**
   * Validate chart data for common issues
   */
  static validate(chartData: { datasets: Array<{ data: ChartDataPoint[] }> }): {
    isValid: boolean;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    // Check for duplicate category names
    const categoryNames = chartData.datasets[0]?.data.map((d) => d.category) ?? [];
    const uniqueCategories = new Set(categoryNames);
    if (categoryNames.length !== uniqueCategories.size) {
      const duplicateCategories = chartData.datasets[0]?.data.filter(
        (item, index) => categoryNames.indexOf(item.category) !== index
      );
      if (duplicateCategories) {
        errors.push(
          `Duplicate categories found: ${duplicateCategories.map((d) => d.category).join(', ')}`
        );
      }
    }

    return {
      isValid: errors.length === 0,
      warnings,
      errors,
    };
  }
}
